import React, { useEffect, useMemo, useState } from "react";
import { fetchMenu, fetchIngredients, fetchRecipe, setRecipe, getCurrency, fetchCostTargets, saveCostTargets } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// F&B Cost Control — Recipes (the hotel's standard portions per dish).
// Manager / Controller. Sets ingredient lines per menu item -> dish cost
// + food cost %.
export default function RecipesPage() {
  const { t } = useT();
  const cur = getCurrency() || "ETB";
  const [menu, setMenu] = useState([]);
  const [ings, setIngs] = useState([]);
  const [menuId, setMenuId] = useState("");
  const [lines, setLines] = useState([]);          // {ingredient_id, quantity}
  const [price, setPrice] = useState(0);
  const [savedCost, setSavedCost] = useState(null);
  const [rec, setRec] = useState(null);            // full recipe response (is_drink, target_pct…)
  const [targets, setTargets] = useState(null);    // {food_cost_target_pct, drink_cost_target_pct}
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([fetchMenu(), fetchIngredients()])
      .then(([m, ig]) => { setMenu(m); setIngs(ig); if (m[0]) setMenuId(String(m[0].id)); })
      .catch((e) => setErr(e?.response?.data?.detail || t("cc_could_not_load")));
    fetchCostTargets().then(setTargets).catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!menuId) return;
    setErr("");
    fetchRecipe(Number(menuId))
      .then((r) => { setRec(r); setLines(r.lines.map((l) => ({ ingredient_id: l.ingredient_id, quantity: l.quantity }))); setPrice(r.price || 0); setSavedCost(r.dish_cost); })
      .catch((e) => setErr(e?.response?.data?.detail || t("cc_could_not_load_recipe")));
  }, [menuId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const saveTargets = (patch) => {
    const next = { ...(targets || {}), ...patch };
    setTargets(next);
    saveCostTargets(patch).then((r) => {
      setTargets(r);
      if (menuId) fetchRecipe(Number(menuId)).then(setRec).catch(() => {});
    }).catch(() => {});
  };

  const ingById = useMemo(() => Object.fromEntries(ings.map((i) => [i.id, i])), [ings]);
  const liveCost = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.quantity) || 0) * ((ingById[l.ingredient_id] || {}).unit_cost || 0), 0),
    [lines, ingById]
  );
  const foodPct = price ? (liveCost / price) * 100 : null;

  // Menu-price health (live, recomputed as lines/price are edited).
  const isDrink = !!(rec && rec.is_drink);
  const targetPct = rec && rec.target_pct != null ? rec.target_pct
    : (targets ? (isDrink ? targets.drink_cost_target_pct : targets.food_cost_target_pct) : (isDrink ? 25 : 30));
  const suggested = liveCost > 0 && targetPct > 0 ? Math.ceil((liveCost / (targetPct / 100)) / 5) * 5 : null;
  const margin = foodPct == null ? null : 100 - foodPct;
  const priceStatus = foodPct == null ? "na" : foodPct <= targetPct ? "good" : foodPct <= targetPct + 10 ? "watch" : "high";
  const STATUS = {
    good:  { color: "#1D9E75", label: t("cc_price_ok") },
    watch: { color: "#854F0B", label: t("cc_price_watch") },
    high:  { color: "#A32D2D", label: t("cc_price_high") },
    na:    { color: "#c2c9d1", label: "" },
  }[priceStatus];

  const addLine = () => {
    const used = new Set(lines.map((l) => l.ingredient_id));
    const next = ings.find((i) => !used.has(i.id));
    if (next) setLines([...lines, { ingredient_id: next.id, quantity: 1 }]);
  };
  const setLine = (idx, patch) => setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const payload = lines.filter((l) => l.ingredient_id && Number(l.quantity) > 0)
        .map((l) => ({ ingredient_id: Number(l.ingredient_id), quantity: Number(l.quantity) }));
      const r = await setRecipe(Number(menuId), payload);
      setSavedCost(r.dish_cost);
    } catch (e) { setErr(e?.response?.data?.detail || t("cc_could_not_save")); }
    finally { setSaving(false); }
  };

  const pctColor = foodPct == null ? "#5f6b78" : foodPct > 40 ? "#A32D2D" : foodPct > 33 ? "#854F0B" : "#1D9E75";

  return (
    <>
      <h1>📋 {t("cc_nav_recipes")} <span className="muted" style={{ fontSize: 14 }}>({t("cc_rec_subtitle")})</span></h1>
      {ings.length === 0 && <div className="card" style={{ marginTop: 8, background: "#FFF8EC", borderColor: "#f0d38b" }}>{t("cc_rec_need_ings")}</div>}

      <div className="card" style={{ marginTop: 12, maxWidth: 680 }}>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("cc_dish")}<br />
          <select value={menuId} onChange={(e) => setMenuId(e.target.value)} style={{ minWidth: 240 }}>
            {menu.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>

        {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}

        <div style={{ marginTop: 14 }}>
          {lines.length === 0 ? <p className="muted">{t("cc_no_lines")}</p> : lines.map((l, idx) => {
            const ing = ingById[l.ingredient_id] || {};
            return (
              <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0" }}>
                <select value={l.ingredient_id} onChange={(e) => setLine(idx, { ingredient_id: Number(e.target.value) })} style={{ flex: "1 1 180px" }}>
                  {ings.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
                <input type="number" min="0" step="0.001" value={l.quantity} onChange={(e) => setLine(idx, { quantity: e.target.value })} style={{ width: 90, textAlign: "right" }} />
                <span className="muted" style={{ width: 36, fontSize: 13 }}>{ing.unit || ""}</span>
                <span style={{ width: 90, textAlign: "right", fontSize: 13, color: "#5f6b78" }}>{formatPrice((Number(l.quantity) || 0) * (ing.unit_cost || 0), cur)}</span>
                <button className="btn secondary small" onClick={() => removeLine(idx)}>✕</button>
              </div>
            );
          })}
          <button className="btn secondary small" style={{ marginTop: 8 }} onClick={addLine} disabled={lines.length >= ings.length}>{t("cc_add_ingredient")}</button>
        </div>

        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-end", marginTop: 16, paddingTop: 12, borderTop: "1px solid #eef2f6" }}>
          <div><div className="muted" style={{ fontSize: 12 }}>{t("cc_dish_cost")}</div><div style={{ fontSize: 22, fontWeight: 700 }}>{formatPrice(liveCost, cur)}</div></div>
          <div><div className="muted" style={{ fontSize: 12 }}>{t("cc_menu_price")}</div><div style={{ fontSize: 22, fontWeight: 700 }}>{formatPrice(price, cur)}</div></div>
          <div>
            <div className="muted" style={{ fontSize: 12 }}>{t("cc_suggested_price")}</div>
            <div style={{ fontSize: 22, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
              {suggested == null ? "—" : formatPrice(suggested, cur)}
              {STATUS.label && <span title={STATUS.label} style={{ width: 11, height: 11, borderRadius: "50%", background: STATUS.color, display: "inline-block" }} />}
            </div>
          </div>
          <div><div className="muted" style={{ fontSize: 12 }}>{t("cc_food_cost_pct")}</div><div style={{ fontSize: 22, fontWeight: 700, color: pctColor }}>{foodPct == null ? "—" : `${foodPct.toFixed(1)}%`}</div></div>
          <div><div className="muted" style={{ fontSize: 12 }}>{t("cc_margin")}</div><div style={{ fontSize: 22, fontWeight: 700, color: "#1D9E75" }}>{margin == null ? "—" : `${margin.toFixed(0)}%`}</div></div>
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={save} disabled={saving}>{saving ? t("cc_saving") : t("cc_save_recipe")}</button>
        </div>
        {STATUS.label && <div style={{ fontSize: 12, marginTop: 6, color: STATUS.color, fontWeight: 600 }}>● {STATUS.label}</div>}
        {savedCost != null && <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{t("cc_saved_std_cost")} {formatPrice(savedCost, cur)}</div>}

        {targets && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px dashed #e3e9ef", display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("cc_food_target")}<br />
              <input type="number" min="1" max="95" step="1" defaultValue={targets.food_cost_target_pct}
                onBlur={(e) => { const v = Number(e.target.value); if (v >= 1 && v <= 95 && v !== targets.food_cost_target_pct) saveTargets({ food_cost_target_pct: v }); }}
                style={{ width: 80, textAlign: "right" }} />
            </label>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("cc_drinks_target")}<br />
              <input type="number" min="1" max="95" step="1" defaultValue={targets.drink_cost_target_pct}
                onBlur={(e) => { const v = Number(e.target.value); if (v >= 1 && v <= 95 && v !== targets.drink_cost_target_pct) saveTargets({ drink_cost_target_pct: v }); }}
                style={{ width: 80, textAlign: "right" }} />
            </label>
            <span className="muted" style={{ fontSize: 11, flex: "1 1 220px" }}>{t("cc_targets_hint")}</span>
          </div>
        )}
      </div>
    </>
  );
}
